import { cn } from '@/lib/utils';
import type { Column } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import * as React from 'react';
import { P3 } from '../ui/text';
import { TableCell } from './table-cell';
import type { TableBodyCellProps } from './types';
import { type PinningShadowDirection, getCommonPinningStyles } from './utils';

// Module-level singleton for text measurement (created once, reused for performance)
let measurementEl: HTMLSpanElement | null = null;

function getMeasurementElement(): HTMLSpanElement {
  if (!measurementEl) {
    measurementEl = document.createElement('span');
    measurementEl.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      pointer-events: none;
      height: 0;
      overflow: hidden;
    `;
    document.body.appendChild(measurementEl);
  }
  return measurementEl;
}

/**
 * Renders a gradient-based shadow for pinned columns.
 * Uses a pseudo-element approach to avoid vertical bleeding that box-shadow causes.
 */
function PinningShadow({ direction }: { direction: PinningShadowDirection }) {
  if (!direction) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute top-0 h-full w-3"
      style={{
        ...(direction === 'left'
          ? { right: 0, transform: 'translateX(100%)' }
          : { left: 0, transform: 'translateX(-100%)' }),
        background:
          direction === 'left'
            ? 'linear-gradient(to right, rgba(0, 44, 42, 0.06) 0%, rgba(0, 44, 42, 0.02) 50%, transparent 100%)'
            : 'linear-gradient(to left, rgba(0, 44, 42, 0.06) 0%, rgba(0, 44, 42, 0.02) 50%, transparent 100%)',
      }}
    />
  );
}

function TableBodyCell<TData>({
  cell,
  rowIndex,
  columnIndex,
  tabIndex,
  pinningShadows,
}: TableBodyCellProps<TData>) {
  const isNavigable = tabIndex !== undefined;
  const skipDefaultStyles = cell.column.columnDef.meta?.skipDefaultCell ?? false;
  const elevateOnFocus = cell.column.columnDef.meta?.elevateOnFocus ?? false;
  const editable = cell.column.columnDef.meta?.editable ?? false;
  const editInputType = cell.column.columnDef.meta?.editInputType ?? 'text';
  const onEditCallback = cell.column.columnDef.meta?.onEdit as
    | ((row: TData, value: string) => boolean | undefined)
    | undefined;
  const onKeyDownCallback = cell.column.columnDef.meta?.onKeyDown as
    | ((e: React.KeyboardEvent, row: TData) => void)
    | undefined;
  const cellContext = cell.getContext();
  const cellContent = flexRender(cell.column.columnDef.cell, cellContext);
  // Check if the cell function returns a React element (custom renderer) vs primitive (default accessor)
  const cellFn = cell.column.columnDef.cell;
  const rawCellResult = typeof cellFn === 'function' ? cellFn(cellContext) : cellFn;
  const hasCustomCell = React.isValidElement(rawCellResult);

  // Editable cell state
  const [isEditing, setIsEditing] = React.useState(false);
  const cellValue = editable ? String(cellContext.getValue() ?? '') : '';
  const [editValue, setEditValue] = React.useState(cellValue);
  const [pendingValue, setPendingValue] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const isSubmittingRef = React.useRef(false);
  const isCancellingRef = React.useRef(false);
  const prevCellValueRef = React.useRef(cellValue);
  const startedWithCharRef = React.useRef(false);

  // Truncation expansion state
  const [isFocused, setIsFocused] = React.useState(false);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Sync edit value when cell value changes
  React.useEffect(() => {
    const cellValueChanged = cellValue !== prevCellValueRef.current;
    prevCellValueRef.current = cellValue;

    // If pending and cellValue changed, the update has propagated (value may be transformed)
    if (pendingValue !== null && cellValueChanged) {
      setPendingValue(null);
      setEditValue(cellValue);
      return;
    }

    // Normal sync when not editing and not pending
    if (!isEditing && pendingValue === null) {
      setEditValue(cellValue);
    }
  }, [cellValue, isEditing, pendingValue]);

  // Focus input when entering edit mode
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Place caret at the end of the text (number inputs don't support setSelectionRange)
      if (editInputType !== 'number') {
        const length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      }
      startedWithCharRef.current = false;
    }
  }, [isEditing]);

  const startEditing = React.useCallback(
    (initialValue?: string) => {
      startedWithCharRef.current = initialValue !== undefined;
      setIsEditing(true);
      setEditValue(initialValue ?? cellValue);
    },
    [cellValue],
  );

  const submitEdit = React.useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    if (editValue !== cellValue && onEditCallback) {
      const result = onEditCallback(cellContext.row.original, editValue);
      // If validation failed (returned false), reset to original value
      if (result === false) {
        setEditValue(cellValue);
      } else {
        // Track pending value until optimistic update propagates
        setPendingValue(editValue);
      }
    }
    setIsEditing(false);
    // Refocus wrapper to maintain keyboard navigation
    wrapperRef.current?.focus();
    // Reset the ref after a tick to allow future submits
    requestAnimationFrame(() => {
      isSubmittingRef.current = false;
    });
  }, [editValue, cellValue, onEditCallback, cellContext.row.original]);

  const cancelEdit = React.useCallback(() => {
    isCancellingRef.current = true;
    setEditValue(cellValue);
    setIsEditing(false);
    wrapperRef.current?.focus();
    // Reset the flag after a tick
    requestAnimationFrame(() => {
      isCancellingRef.current = false;
    });
  }, [cellValue]);

  // Measure content to detect if it's truncated
  const checkTruncation = React.useCallback(() => {
    const content = contentRef.current;
    if (!content) {
      setIsTruncated(false);
      return;
    }

    // Check if element's text content exceeds its available width
    const checkElement = (el: HTMLElement): boolean => {
      // Fast path: scrollWidth check (works for flex layouts without explicit width)
      if (el.scrollWidth > el.clientWidth) {
        return true;
      }

      // Only do expensive measurement for elements with truncation styles
      const styles = getComputedStyle(el);
      const hasTruncation = styles.overflow === 'hidden' && styles.textOverflow === 'ellipsis';
      if (!hasTruncation) {
        return false;
      }

      const text = el.textContent?.trim();
      if (!text) {
        return false;
      }

      // Reuse singleton measurement element for performance
      const measureEl = getMeasurementElement();
      measureEl.style.font = styles.font;
      measureEl.style.letterSpacing = styles.letterSpacing;
      measureEl.style.wordSpacing = styles.wordSpacing;
      measureEl.style.textTransform = styles.textTransform;
      measureEl.textContent = text;

      const textWidth = measureEl.offsetWidth;
      const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
      const availableWidth = el.clientWidth - paddingLeft - paddingRight;

      return textWidth > availableWidth;
    };

    // Check the content container itself
    if (checkElement(content)) {
      setIsTruncated(true);
      return;
    }

    // Check all child elements - early exit on first match
    const allElements = content.querySelectorAll('*');
    for (const el of allElements) {
      if (el instanceof HTMLElement && checkElement(el)) {
        setIsTruncated(true);
        return;
      }
    }

    setIsTruncated(false);
  }, []);

  // Handle focus/blur for truncation expansion
  const handleFocus = React.useCallback(() => {
    setIsFocused(true);
    // Don't expand if editing (editable cells have their own expansion) or skipDefaultStyles
    if (!isEditing && !skipDefaultStyles) {
      // Measure on next frame to ensure content is rendered
      requestAnimationFrame(checkTruncation);
    }
  }, [isEditing, skipDefaultStyles, checkTruncation]);

  const handleBlur = React.useCallback((e: React.FocusEvent) => {
    // Only clear focus state if focus is leaving the cell entirely
    if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
      setIsTruncated(false);
    }
  }, []);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (isEditing) {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          submitEdit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          cancelEdit();
        } else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
          // Stop propagation to prevent table navigation while editing
          // Let the native input handle cursor movement
          e.stopPropagation();
        }
      } else if (editable) {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          startEditing();
        } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          // Printable character - start editing with that character
          e.preventDefault();
          e.stopPropagation();
          startEditing(e.key);
        }
      }
    },
    [isEditing, editable, submitEdit, cancelEdit, startEditing],
  );

  const handleEditBlur = React.useCallback(() => {
    // Don't submit if we're cancelling (ESC was pressed)
    if (isEditing && !isCancellingRef.current) {
      submitEdit();
    }
  }, [isEditing, submitEdit]);

  // Check if we should show expanded content
  const shouldExpand = isFocused && isTruncated && !isEditing;

  // Get pinning styles and shadow direction
  const { styles: pinningStyles, shadowDirection } = getCommonPinningStyles(
    cell.column as Column<any>,
    pinningShadows,
  );

  // Render content based on cell type
  const renderContent = () => {
    // Skip default styles - render custom cell content (with ref wrapper for measurement)
    if (skipDefaultStyles) {
      return (
        <div ref={contentRef} className="w-full">
          {cellContent}
        </div>
      );
    }

    // Editable cell - toggle between input and text
    if (editable && onEditCallback) {
      if (isEditing) {
        return (
          <TableCell className="absolute left-0 top-0 w-[150%] rounded-xs border-highlight-default group-focus-within:bg-white group-focus:bg-white">
            <input
              ref={inputRef}
              type={editInputType}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleEditBlur}
              className="w-full bg-transparent font-medium text-primary-100 text-s caret-primary-100 outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
            />
          </TableCell>
        );
      }
      // Show pendingValue while waiting for optimistic update, then show custom cell
      const showCustomCell = hasCustomCell && pendingValue === null;
      const displayValue = pendingValue ?? editValue;
      return (
        <>
          {/* Base cell for measuring truncation */}
          <div ref={contentRef}>
            <TableCell
              className="h-10 cursor-pointer truncate"
              onDoubleClick={() => startEditing()}
            >
              {showCustomCell ? (
                cellContent
              ) : (
                <P3 className="w-full truncate text-left">{displayValue}</P3>
              )}
            </TableCell>
          </div>
          {/* Expanded cell overlay when truncated */}
          {shouldExpand && (
            <TableCell
              className="absolute left-0 top-0 w-[150%] truncate overflow-hidden rounded-xs border-highlight-default bg-white"
              onDoubleClick={() => startEditing()}
            >
              {showCustomCell ? (
                <div className="w-full truncate whitespace-nowrap">{cellContent}</div>
              ) : (
                <P3 className="w-full truncate whitespace-nowrap text-left">{displayValue}</P3>
              )}
            </TableCell>
          )}
        </>
      );
    }

    // Default cell rendering - apply disabled styling based on column meta
    const isColumnDisabled = cell.column.columnDef.meta?.disabled ?? false;
    return (
      <>
        {/* Base cell for measuring truncation */}
        <div ref={contentRef}>
          <TableCell className="h-10 truncate" isDisabled={isColumnDisabled}>
            {hasCustomCell ? (
              cellContent
            ) : (
              <P3 className="w-full truncate text-left">{cellContent}</P3>
            )}
          </TableCell>
        </div>
        {/* Expanded cell overlay when truncated */}
        {shouldExpand && (
          <TableCell
            className="absolute left-0 top-0 w-[150%] overflow-hidden rounded-xs border-highlight-default bg-white"
            isDisabled={isColumnDisabled}
          >
            {hasCustomCell ? (
              <div className="w-full truncate whitespace-nowrap">{cellContent}</div>
            ) : (
              <P3 className="w-full truncate whitespace-nowrap text-left">{cellContent}</P3>
            )}
          </TableCell>
        )}
      </>
    );
  };

  return (
    <div
      ref={wrapperRef}
      data-cell-row={rowIndex}
      data-cell-col={columnIndex}
      className={cn('virtual-table-cell group focus:outline-none', isNavigable && 'cursor-pointer')}
      style={{
        width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
        left: `calc(var(--col-${cell.column.id}-start) * 1px)`,
        ...pinningStyles,
        // Z-index hierarchy:
        // - Editing/expanding: pinned=25, non-pinned=15
        // - Focused + elevateOnFocus: pinned=21, non-pinned=15 (keeps non-pinned below pinned columns)
        // - Default: from pinningStyles (pinned=20, non-pinned=0)
        ...(isEditing || shouldExpand
          ? { zIndex: cell.column.getIsPinned() ? 25 : 15 }
          : isFocused && elevateOnFocus
            ? { zIndex: cell.column.getIsPinned() ? 21 : 15 }
            : {}),
      }}
      tabIndex={tabIndex}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={
        editable || onKeyDownCallback
          ? (e) => {
              if (editable) {
                handleKeyDown(e);
              }
              if (onKeyDownCallback) {
                onKeyDownCallback(e, cellContext.row.original);
              }
            }
          : undefined
      }
    >
      {renderContent()}
      <PinningShadow direction={shadowDirection} />
    </div>
  );
}

// Memoize cell to prevent unnecessary re-renders
// Custom memo comparison - don't skip re-renders for editable cells to ensure rollbacks work
export const MemoizedTableBodyCell = React.memo(TableBodyCell, (prevProps, nextProps) => {
  const isEditable = nextProps.cell.column.columnDef.meta?.editable;
  if (isEditable) {
    // Always re-render editable cells to catch value changes (including rollbacks)
    return false;
  }
  // Default shallow comparison for non-editable cells
  const keys = Object.keys(prevProps) as Array<keyof typeof prevProps>;
  return keys.every((key) => prevProps[key] === nextProps[key]);
}) as typeof TableBodyCell;
